import React, { useEffect, useState } from "react";
import { Container } from "@mui/material";
import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";
import dayjs from "dayjs";
import Select from "@mui/material/Select";
import Avatar from "@mui/material/Avatar";

import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import InputLabel from "@mui/material/InputLabel";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";

import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";

import DrawerAppBar from "../../components/DrawerAppBar";
import Title from "../../components/Title";
import ProgressBar from "../../components/ProgressBar";

import { generateUUID } from "../../services/AbstractService";

import {
  searchActivities,
  getCategories,
} from "../../services/ActivityService";
import { formatDayJsDate } from "../../services/ServiceHelper";
import { hasError, getError } from "../../services/ServiceHelper";
import {
  getSchoolTeachers,
  getPeriodName,
  getSchoolTags,
} from "../../services/SchoolService";

const ActivitySearch = () => {
  const [school] = useLocalStorage("Auth", "school", null);
  const [_jwt] = useJwtStorage();

  const [_showProgress, setShowProgress] = useState(false);
  const [_backDropOpen, setBackDropOpen] = useState(false);

  const [_searchPerformed, setSearchPerformed] = useState(false);

  const [_teachers, setTeachers] = useState([]);
  const [_teacherEid, setTeacherEid] = useState("XX");

  const [_categories, setCategories] = useState([]);
  const [_categoryEid, setCategoryEid] = useState("XX");
  const [_period, setPeriod] = useState("1");

  const [_dateStart, setDateStart] = useState(dayjs());
  const [_dateEnd, setDateEnd] = useState(dayjs().add(14, "day"));

  const [_selectedIndex, setSelectedIndex] = React.useState(0);

  const [_tags, setTags] = useState([]);
  const [_selectedTagEid, setSelectedTagEid] = React.useState("XX");

  const [activities, setActivities] = useState([]);

  useEffect(() => {
    setBackDropOpen(true);
    setShowProgress(true);

    const promises = [initTeachers(), initCategories(), initTags()];

    Promise.all(promises)
      .then((results) => {
        let result = results[0];
        if (hasError(result.data) === true) {
          window.showSnack("Unable to get Teachers: " + getError(result.data));
          return;
        }
        setTeachers(result.data);

        result = results[1];
        if (hasError(result.data) === true) {
          window.showSnack(
            "Unable to get Categories: " + getError(result.data)
          );
          return;
        }
        setCategories(result.data);

        result = results[2];
        setTags(result.data);
      })
      .finally(() => {
        setShowProgress(false);
        setBackDropOpen(false);
      });
  }, []);

  const initTags = () => {
    return getSchoolTags(school, _jwt);
  };

  const initCategories = () => {
    return getCategories(school, _jwt);
  };

  const initTeachers = () => {
    return getSchoolTeachers(school, _jwt);
  };

  const handleSearch = () => {
    setSearchPerformed(true);

    setShowProgress(true);

    searchActivities(
      school,
      _jwt,
      formatDayJsDate(_dateStart),
      formatDayJsDate(_dateEnd),
      _categoryEid,
      _teacherEid,
      _period,
      _selectedTagEid
    )
      .then((results) => {
        if (hasError(results) === true) {
          window.showSnack(
            "Unable to get Search Activities: " + getError(results)
          );
          return;
        }

        setActivities(results.data);
        setSelectedIndex(1);
      })
      .finally(() => {
        setShowProgress(false);
      });
  };

  const handleChangeTeacher = (event) => setTeacherEid(event.target.value);
  const handleChangeCategory = (event) => setCategoryEid(event.target.value);
  const handleChangePeriod = (event) => setPeriod(event.target.value);
  const handleChangeTag = (event) => setSelectedTagEid(event.target.value);

  const onTabChange = (event, index) => setSelectedIndex(index);

  return (
    <>
      <DrawerAppBar />

      <Container sx={{ mt: 1 }}>
        <Title text="Activity Search" showBack={true} />
        <ProgressBar showProgress={_showProgress} />

        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={_selectedIndex} onChange={onTabChange}>
            <Tab label="Criteria" />
            <Tab label="Results" />
          </Tabs>
        </Box>

        {_selectedIndex === 0 && (
          <Box sx={{ boxShadow: 1, p: 1, mt: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <InputLabel>Start Date</InputLabel>
                <MobileDatePicker
                  value={_dateStart}
                  onChange={(newValue) => {
                    setDateStart(newValue);
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <InputLabel>End Date</InputLabel>
                <MobileDatePicker
                  value={_dateEnd}
                  onChange={(newValue) => {
                    setDateEnd(newValue);
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <InputLabel>Category</InputLabel>
                <Select
                  id="categoryEid"
                  size="small"
                  onChange={handleChangeCategory}
                  value={_categoryEid}
                  fullWidth
                >
                  <MenuItem value="XX">-- ANY CAT --</MenuItem>
                  {_categories.map((cat) => (
                    <MenuItem key={cat.eid} value={cat.eid}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={6}>
                <InputLabel>Period</InputLabel>
                <Select
                  id="period"
                  size="small"
                  onChange={handleChangePeriod}
                  value={_period}
                  fullWidth
                >
                  {Array.from(Array(school.maxActivityCount), (e, i) => {
                    return (
                      <MenuItem key={i + 1} value={i + 1}>
                        {getPeriodName(school, i)}
                      </MenuItem>
                    );
                  })}
                </Select>
              </Grid>
              <Grid item xs={6}>
                <InputLabel>Tag</InputLabel>
                <Select
                  id="period"
                  size="small"
                  onChange={handleChangeTag}
                  value={_selectedTagEid}
                  fullWidth
                >
                  <MenuItem value="XX">-- ANY TAG --</MenuItem>
                  {_tags.map((tag) => (
                    <MenuItem key={tag.eid} value={tag.eid}>
                      {tag.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={12}>
                <InputLabel>Teacher</InputLabel>
                <Select
                  id="teacherEid"
                  size="small"
                  onChange={handleChangeTeacher}
                  value={_teacherEid}
                  fullWidth
                >
                  <MenuItem value="XX">-- ANY TEACHER --</MenuItem>
                  {_teachers.map((teacher) => (
                    <MenuItem key={teacher.eid} value={teacher.eid}>
                      {teacher.last_name}, {teacher.first_name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </Grid>
            <Button onClick={handleSearch} sx={{ mt: 2 }} variant="contained">
              Search
            </Button>
          </Box>
        )}
        {_selectedIndex === 1 && (
          <>
            {_searchPerformed === true && (
              <Box sx={{ boxShadow: 1, p: 1, mt: 1 }}>
                <Table sx={{ minWidth: 340 }}>
                  <TableBody>
                    {activities.map((a) => (
                      <TableRow
                        key={generateUUID()}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell
                          sx={{ pr: 0, pl: 0, pt: 1, verticalAlign: "top" }}
                        >
                          <Avatar
                            src={`https://alltimely-public.s3.amazonaws.com/global/image/icons/${a.iconPath}`}
                          />
                        </TableCell>
                        <TableCell sx={{ pt: 0, pb: 1 }}>
                          <strong>
                            {a.date} - {getPeriodName(school, a.period - 1)}
                          </strong>
                          <br />
                          {a.name}
                          <br />
                          LOC: {a.locationName}
                          <br />
                          PROCTOR: {a.teacherLastName}
                        </TableCell>
                      </TableRow>
                    ))}
                    {activities.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          -- NO RESULTS RETURNED --
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            )}
            {_searchPerformed === false && (
              <Container sx={{ mt: 2, pl: 0, textAlign: "center" }}>
                -- CLICK SEARCH FOR RESULTS --
              </Container>
            )}
          </>
        )}

        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={_backDropOpen}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Container>
    </>
  );
};

export default ActivitySearch;
